import { getObjectId, type Route } from '@hero/schema';
import type { RouteCreateAPI, RouteUpdateAPI } from '@hero/api';
import { getScopeAgreementId, getScopeCounterparty } from './types';
import type { SheetRow, SheetScope, SheetSide } from './types';

/**
 * Convert a single `Route` into a row the sheet can render and edit.
 *
 * `id` doubles as the sheet's local row key. For persisted rows we reuse the
 * backend `_id` so selection survives re-fetches; for newly-added rows the
 * page uses a UUID generated by `mockRows.makeSheetRowId`.
 */
export function routeToSheetRow(route: Route): SheetRow {
  const destinations = route.destinations ?? [];
  const origin = destinations[0] ?? null;
  const destination =
    destinations.length > 1 ? destinations[destinations.length - 1] : null;

  return {
    id: String(route._id),
    routeId: String(route._id),
    origin,
    destination,
    vehicles: [...route.vehicles],
    tripType: route.tripType,
    loadType: route.loadType,
    temperatureType: [...route.temperatureType],
    price: route.price,
    status: 'unchanged',
  };
}

/**
 * Resolve `sellerId` / `buyerId` for a row given the sheet-wide `side` +
 * `scope`. Mirrors the structure of `RouteEditModal.getDirectedParties` so
 * the sheet stays consistent with the modal flow.
 *
 * Truth table (read `me` as the current org):
 *
 *   | scope.kind   | side='sell'                          | side='buy'                           |
 *   | ------------ | ------------------------------------ | ------------------------------------ |
 *   | 'own'        | { sellerId: me }                     | { buyerId: me }                      |
 *   | 'direct'     | { sellerId: me, buyerId: other }     | { sellerId: other, buyerId: me }     |
 *   | 'agreement'  | { sellerId: me, buyerId: other }     | { sellerId: other, buyerId: me }     |
 *
 * `'direct'` and `'agreement'` produce the same `(seller, buyer)` shape;
 * the only difference is that `'agreement'` additionally carries
 * `agreementId` (see {@link rowToCreatePayload}), which triggers the
 * stricter parties-match validation on the backend.
 *
 * `isOwnRoute` is **server-derived** in `RouteService.normalize` (it's
 * `!seller || !buyer`) — never set it on the client payload.
 */
function getDirectedParties(side: SheetSide, orgId: string, scope: SheetScope) {
  const meId = getObjectId(orgId);
  const counterparty = getScopeCounterparty(scope);
  const otherId = counterparty ? getObjectId(counterparty.id) : undefined;
  return side === 'sell'
    ? { sellerId: meId, buyerId: otherId }
    : { sellerId: otherId, buyerId: meId };
}

/**
 * Required row data for a save. We extract the API-ready destinations array
 * once so both create + update payloads share the same shape.
 */
function compactDestinations(row: SheetRow): RouteCreateAPI['req']['destinations'] {
  // The sheet always renders exactly 2 endpoints (origin + destination); the
  // resolver requires `min(2)`, so we omit invalid rows upstream and trust the
  // values to be present here.
  const all = [row.origin, row.destination].filter(
    (r): r is NonNullable<typeof r> => Boolean(r),
  );
  return all.map(d => ({
    _id: getObjectId(String(d._id)),
    displayName: (d as { displayName?: string }).displayName,
    type: (d as { type?: string }).type,
  })) as RouteCreateAPI['req']['destinations'];
}

export function rowToCreatePayload(
  row: SheetRow,
  side: SheetSide,
  orgId: string,
  scope: SheetScope,
): RouteCreateAPI['req'] {
  const { sellerId, buyerId } = getDirectedParties(side, orgId, scope);
  // Threaded through only for `'agreement'` scope. The backend rejects
  // `agreementId` when both parties are not also set + matching
  // `Agreement.parties[]`, but `getDirectedParties` guarantees both ids are
  // set whenever `agreementId` is, so we never trip that rule from here.
  const agreementId = getScopeAgreementId(scope);
  return {
    sellerId,
    buyerId,
    agreementId: agreementId ? getObjectId(agreementId) : undefined,
    loadType: row.loadType,
    tripType: row.tripType,
    vehicles: row.vehicles,
    temperatureType: row.temperatureType,
    destinations: compactDestinations(row),
    price: row.price,
  };
}

export function rowToUpdatePayload(
  row: SheetRow,
  side: SheetSide,
  orgId: string,
  scope: SheetScope,
): RouteUpdateAPI['req'] {
  if (!row.routeId) {
    throw new Error('rowToUpdatePayload called on a row without a persisted id');
  }
  const { sellerId, buyerId } = getDirectedParties(side, orgId, scope);
  const agreementId = getScopeAgreementId(scope);
  return {
    _id: getObjectId(row.routeId),
    sellerId,
    buyerId,
    agreementId: agreementId ? getObjectId(agreementId) : undefined,
    loadType: row.loadType,
    tripType: row.tripType,
    vehicles: row.vehicles,
    temperatureType: row.temperatureType,
    destinations: compactDestinations(row),
    price: row.price,
  };
}

/**
 * A row is savable when it has the minimum fields the create schema requires.
 * We use this both to gate "Save all" (skipping incomplete drafts silently)
 * and to count how many pending rows are actually persistable.
 */
export function isRowSavable(row: SheetRow): boolean {
  return (
    row.origin !== null &&
    row.destination !== null &&
    row.vehicles.length > 0 &&
    row.temperatureType.length > 0 &&
    row.price > 0
  );
}
